import { useState } from 'react'
import { Link } from 'react-router-dom'
import ShipToPicker from '@/components/orders/ShipToPicker'
import Icon from '@/components/common/Icons'

/**
 * Who the order is for: enough to ring them, deliver to them and bill them
 * without leaving the order. The full record — tier, credit, notes — stays
 * on the customer's own page, linked from the title.
 */
export default function CustomerPanel({ customer, order, canChangeShipTo = false }) {
  const [picking, setPicking] = useState(false)
  if (!customer) return null

  const address = [
    customer.address_line1,
    customer.address_line2,
    [customer.city, customer.state].filter(Boolean).join(', '),
    customer.pincode,
  ].filter(Boolean)
  // The shipping address only when it says something the address does not —
  // or always, where staff may change it, so there is a row to change.
  const shipping =
    customer.shipping_address &&
    (canChangeShipTo || customer.shipping_address !== address.join(', '))
      ? customer.shipping_address
      : null

  return (
    <div className="customer-panel">
      <div className="panel-title panel-title-row">
        Customer
        <Link to={`/customers/${customer.id}`} className="panel-link">
          View customer
        </Link>
      </div>
      <dl className="kv">
        <dt>Name</dt>
        <dd>
          {customer.name}
          {customer.business_name && <div className="muted">{customer.business_name}</div>}
        </dd>

        <dt>WhatsApp</dt>
        <dd>
          <a href={`https://wa.me/${customer.phone}`} target="_blank" rel="noreferrer">
            {customer.phone}
          </a>
        </dd>

        {customer.other_phones?.length > 0 && (
          <>
            <dt>Other phones</dt>
            <dd>
              {customer.other_phones.map((phone) => (
                <div key={phone}>
                  <a href={`tel:+${phone}`}>{phone}</a>
                </div>
              ))}
            </dd>
          </>
        )}

        {address.length > 0 && (
          <>
            <dt>Address</dt>
            <dd>
              {address.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </dd>
          </>
        )}

        {(shipping || canChangeShipTo) && (
          <>
            <dt>Ship to</dt>
            <dd className="ship-to-row">
              <span className="pre-line">
                {customer.shipping_label && <strong>{customer.shipping_label}</strong>}
                {customer.shipping_label && '\n'}
                {shipping || <span className="muted">Not set</span>}
              </span>
              {canChangeShipTo && !picking && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon ship-to-change"
                  title={shipping ? 'Change shipping address' : 'Add shipping address'}
                  aria-label={shipping ? 'Change shipping address' : 'Add shipping address'}
                  onClick={() => setPicking(true)}
                >
                  <Icon name="edit" />
                </button>
              )}
            </dd>
          </>
        )}

        {customer.gstin && (
          <>
            <dt>GSTIN</dt>
            <dd className="mono">{customer.gstin}</dd>
          </>
        )}

        {customer.email && (
          <>
            <dt>Email</dt>
            <dd>
              <a href={`mailto:${customer.email}`}>{customer.email}</a>
            </dd>
          </>
        )}
      </dl>
      {picking && order && <ShipToPicker order={order} onClose={() => setPicking(false)} />}
    </div>
  )
}
